import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ProductCard } from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const { isAuthenticated, token } = useAuth();

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'men', label: 'Men' },
    { value: 'women', label: 'Women' },
    { value: 'kids', label: 'Kids' },
    { value: 'accessories', label: 'Accessories' },
  ];

  useEffect(() => {
    fetchProducts();
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [category, sortBy]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = category && category !== 'all' 
        ? `${API}/products/category/${category}`
        : `${API}/products`;
      const response = await axios.get(url);
      let sorted = [...response.data];
      
      if (sortBy === 'price-low') {
        sorted.sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-high') {
        sorted.sort((a, b) => b.price - a.price);
      } else if (sortBy === 'name') {
        sorted.sort((a, b) => a.name.localeCompare(b.name));
      }
      
      setProducts(sorted);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data.map(p => p.id));
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  const handleAddToWishlist = async (productId) => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    try {
      if (wishlist.includes(productId)) {
        await axios.delete(`${API}/wishlist/remove/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist(prev => prev.filter(id => id !== productId));
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, { product_id: productId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist(prev => [...prev, productId]);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const getCategoryTitle = () => {
    if (!category || category === 'all') return 'All Products';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="shop-page">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-12">
          <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Shop</span>
          <h1 className="font-['Syne'] text-3xl md:text-5xl font-bold mt-2">{getCategoryTitle()}</h1>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-8 border-b border-neutral-800">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={(!category && cat.value === 'all') || category === cat.value ? 'default' : 'outline'}
                className={`rounded-none uppercase text-xs tracking-widest ${
                  (!category && cat.value === 'all') || category === cat.value
                    ? 'bg-white text-black'
                    : 'border-neutral-700 hover:bg-white hover:text-black'
                }`}
                onClick={() => {
                  if (cat.value === 'all') {
                    window.location.href = '/shop';
                  } else {
                    window.location.href = `/shop/${cat.value}`;
                  }
                }}
                data-testid={`filter-${cat.value}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center space-x-4">
            <span className="text-muted-foreground text-sm">{products.length} products</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] rounded-none bg-neutral-900 border-neutral-800" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 border-neutral-800">
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="name">Name A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-900 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="font-['Syne'] text-xl font-bold mb-2">No products found</h3>
            <p className="text-muted-foreground">Check back soon for new arrivals</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToWishlist={handleAddToWishlist}
                isInWishlist={wishlist.includes(product.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
